import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
    Paper,
    Stack,
    InputAdornment,
    IconButton,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Tooltip,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    Grid,
    Alert,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import PercentIcon from '@mui/icons-material/Percent';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BusinessIcon from '@mui/icons-material/Business';
import InfoIcon from '@mui/icons-material/Info';
import { request } from '../../../../utils/request';
import { toast } from 'react-toastify';
import Brandcrumb from '../../../../componentsUiBlock/Brandcrumb';
import PriceCodeDisplay from '../../../controlPanel/components/PriceCodeDisplay.tsx';
import { useDiscountCalculator } from '../../../../utils/useDiscountCalculator';
import { mapPriceListToProduct } from '../../../../utils/priceListMapper';
import { AttributeField, Brand, CarModel, Category, NavigationSource, ProductFormData, SelectedCompatibility, Vendor } from '../../types';
import { baseUrlImage } from '../../../../utils/apiClient';
import { useImageCompression } from '../../../../utils/useImageCompression';

const AddProduct: React.FC = () => {
    const { compressMultipleImages, isCompressing } = useImageCompression();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState<Category[]>([]);
    const [, setAllBrands] = useState<Brand[]>([]);
    const [carBrands, setCarBrands] = useState<Brand[]>([]);
    const [partBrands, setPartBrands] = useState<Brand[]>([]);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [carModels, setCarModels] = useState<CarModel[]>([]);
    const [filteredCarModels, setFilteredCarModels] = useState<CarModel[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [attributeFields, setAttributeFields] = useState<AttributeField[]>([]);
    const [imageFiles, setImageFiles] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [selectedCompatibility, setSelectedCompatibility] = useState<SelectedCompatibility[]>([]);
    const [selectedCarBrand, setSelectedCarBrand] = useState<Brand | null>(null);
    const [selectedCarModel, setSelectedCarModel] = useState<CarModel | null>(null);
    const [selectedVariants, setSelectedVariants] = useState<string[]>([]);
    const [selectedFuelTypes, setSelectedFuelTypes] = useState<string[]>([]);
    const [selectedTransmissions, setSelectedTransmissions] = useState<string[]>([]);
    const [selectedGenerations, setSelectedGenerations] = useState<string[]>([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [loadingVendors, setLoadingVendors] = useState(false);
    const [loadingModels, setLoadingModels] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingCompatibilityIndex, setEditingCompatibilityIndex] = useState<number | null>(null);
    const [brandSearchInput, setBrandSearchInput] = useState('');
    const [modelSearchInput, setModelSearchInput] = useState('');
    const [categorySearchInput, setCategorySearchInput] = useState('');
    const [vendorSearchInput, setVendorSearchInput] = useState('');
    const [descriptionJsonFields, setDescriptionJsonFields] = useState<Array<{ key: string; value: string }>>([]);
    const [newJsonField, setNewJsonField] = useState<{ key: string; value: string }>({ key: "", value: "" });

    // Enhanced navigation state
    const [navigationSource, setNavigationSource] = useState<NavigationSource>({ type: 'inventory' });
    const [loadingPreFill, setLoadingPreFill] = useState(false);
    const [priceListData, setPriceListData] = useState<any>(null);

    const [sourceType, setSourceType] = useState<'manual' | 'price-list' | 'oem' | 'oes' | 'import' | 'lot'>('manual');
    const [sourceId, setSourceId] = useState('');
    const [importBatchId, setImportBatchId] = useState('');
    const [sourceMetadata, setSourceMetadata] = useState<Array<{ key: string; value: string }>>([]);
    const [newSourceMetadata, setNewSourceMetadata] = useState<{ key: string; value: string }>({ key: "", value: "" });

    const { calculatePrice, calculateUnitPrice } = useDiscountCalculator();

    const {
        register,
        handleSubmit,
        control,
        setValue,
        watch,
        reset,
        formState: { errors },
    } = useForm<ProductFormData>({
        defaultValues: {
            name: '',
            partNo: '',
            barcode: '',
            productImages: [],
            quantity: 0,
            category: '',
            brand: '',
            vender: '',
            mrp: "",
            unitPrice: "",
            purchaseDiscount: "",
            purchasePrice: "",
            discountB2C: "",
            sellingPriceB2C: "",
            discountB2B: "",
            sellingPriceB2B: "",
            description: { text: "", jsonFields: {} },
            compatibility: [],
            attributes: {},
            status: 'active',
            source: {
                type: 'manual',
                id: '',
                date: new Date(),
                metadata: {}
            },
            importBatchId: ''
        },
    });

    const watchedCategory = watch('category');

    // Check navigation state on component mount
    useEffect(() => {
        // Function to fetch car data and pre-fill compatibility
        const fetchCarData = async (carId: string) => {
            try {
                const res = await request({
                    url: `/compatibility/get-car-model/${carId}`,
                    method: "get",
                });

                if (res.carModel) {
                    const carData = res.carModel;

                    // Transform car data to compatibility format
                    const compatibilityData: SelectedCompatibility = {
                        carModel: carData._id,
                        carName: carData.name,
                        brandName: carData.brand.name,
                        baseImage: carData.baseImage || carData.brand.logo,
                        variants: carData.variants,
                        fuelTypes: carData.fuelTypes,
                        transmissions: carData.transmissions,
                        generations: carData.generations.map((gen: { from: any; to: any; }) => `${gen.from}-${gen.to}`),
                    };

                    // Auto-populate the compatibility section
                    setSelectedCompatibility([compatibilityData]);

                    // Pre-fill product name suggestion
                    setValue('name', `${carData.brand.name} ${carData.name} Part`);

                    toast.success(`Compatibility pre-filled for ${carData.brand.name} ${carData.name}`);
                }
            } catch (error) {
                console.error('Failed to fetch car data for pre-fill:', error);
                toast.error('Failed to load car compatibility data');
            }
        };

        // Function to fetch category data and pre-fill category
        const fetchCategoryData = async (categoryId: string) => {
            try {
                const res = await request({
                    url: `/category/get-category/${categoryId}`,
                    method: "get",
                });

                if (res.category) {
                    const categoryData = res.category;

                    // Pre-fill category
                    setValue('category', categoryData.name);

                    // Pre-fill product name suggestion
                    setValue('name', `${categoryData.name} Product`);

                    toast.success(`Category pre-filled: ${categoryData.name}`);
                }
            } catch (error) {
                console.error('Failed to fetch category data for pre-fill:', error);
                toast.error('Failed to load category data');
            }
        };

        // Function to fetch brand data and pre-fill brand
        const fetchBrandData = async (brandId: string) => {
            try {
                const res = await request({
                    url: `/brand/get-one-brand/${brandId}`,
                    method: "get",
                });

                if (res.brand) {
                    const brandData = res.brand;

                    // Pre-fill brand based on manufacture type
                    if (brandData.manufactureType.includes('Parts')) {
                        setValue('brand', brandData.name);
                    }

                    // Pre-fill product name suggestion
                    setValue('name', `${brandData.name} Product`);

                    toast.success(`Brand pre-filled: ${brandData.name}`);
                }
            } catch (error) {
                console.error('Failed to fetch brand data for pre-fill:', error);
                toast.error('Failed to load brand data');
            }
        };

        // Function to fetch vendor data and pre-fill vendor
        const fetchVendorData = async (vendorId: string) => {
            try {
                const res = await request({
                    url: `/vendor/get-vendor/${vendorId}`,
                    method: "get",
                });

                if (res.vendor) {
                    const vendorData = res.vendor;

                    // Pre-fill vendor
                    setValue('vender', vendorData.vendorName);

                    // Pre-fill product name suggestion
                    setValue('name', `${vendorData.vendorName} Product`);

                    // If vendor has brands, suggest the first one
                    if (vendorData.brands && vendorData.brands.length > 0) {
                        setValue('brand', vendorData.brands[0]);
                    }

                    toast.success(`Vendor pre-filled: ${vendorData.vendorName}`);
                }
            } catch (error) {
                console.error('Failed to fetch vendor data for pre-fill:', error);
                toast.error('Failed to load vendor data');
            }
        };

        // Function to fetch price list data
        // Function to fetch price list data
        const fetchPriceListData = async (priceListItemId: string) => {
            try {
                setLoadingPreFill(true);
                const res = await request({
                    url: `priceList/get-one-pricelist/${priceListItemId}`,
                    method: "get",
                });

                if (res.success && res.data) {
                    const priceListDataApi = res.data;
                    setPriceListData(priceListDataApi);

                    // Map price list data to product using utility
                    const mapping = mapPriceListToProduct(priceListDataApi, priceListItemId);

                    // Set ALL mapped product fields
                    const fields = mapping.productFields;

                    if (fields.name) {
                        setValue('name', fields.name);
                    }

                    if (fields.partNo) {
                        setValue('partNo', fields.partNo);
                    }

                    if (fields.vender) {
                        setValue('vender', fields.vender);
                    }

                    if (fields.mrp) {
                        setValue('mrp', fields.mrp);
                    }

                    if (fields.unitPrice !== undefined) {
                        setValue('unitPrice', fields.unitPrice);
                    }

                    if (fields.brand) {
                        setValue('brand', fields.brand);
                    }

                    if (fields.category) {
                        setValue('category', fields.category);
                    }

                    if (fields.purchaseDiscount !== undefined) {
                        setValue('purchaseDiscount', fields.purchaseDiscount);
                    }

                    if (fields.purchasePrice !== undefined) {
                        setValue('purchasePrice', fields.purchasePrice);
                    }

                    if (fields.discountB2C !== undefined) {
                        setValue('discountB2C', fields.discountB2C);
                    }

                    if (fields.sellingPriceB2C !== undefined) {
                        setValue('sellingPriceB2C', fields.sellingPriceB2C);
                    }

                    if (fields.discountB2B !== undefined) {
                        setValue('discountB2B', fields.discountB2B);
                    }

                    if (fields.sellingPriceB2B !== undefined) {
                        setValue('sellingPriceB2B', fields.sellingPriceB2B);
                    }

                    if (fields.quantity !== undefined) {
                        setValue('quantity', fields.quantity);
                    }

                    // Set description TEXT if it exists in price list
                    if (mapping.descriptionText) {
                        setValue('description.text', mapping.descriptionText);
                    }

                    // Set description fields - ALL JSON fields from price list
                    if (mapping.descriptionFields.length > 0) {
                        setDescriptionJsonFields(mapping.descriptionFields);
                    }

                    // Set source information
                    setSourceType('price-list');
                    setSourceId(priceListItemId);
                    setSourceMetadata(mapping.sourceMetadata);

                    toast.success('Price list data loaded successfully');
                }
            } catch (error: any) {
                console.error('Failed to fetch price list data:', error);
                toast.error(error || 'Failed to load price list data');
            } finally {
                setLoadingPreFill(false);
            }
        };

        const checkNavigationState = async () => {
            const state = location.state as {
                source?: string;
                carId?: string;
                categoryId?: string;
                brandId?: string;
                vendorId?: string;
                priceListItemId?: string;
                pricelistItemId?: string;
            } | undefined;

            if (!state?.source) {
                setNavigationSource({ type: 'inventory' });
                return;
            }

            try {
                setLoadingPreFill(true);

                switch (state.source) {
                    case 'car-details':
                        if (state.carId) {
                            setNavigationSource({ type: 'car', id: state.carId });
                            await fetchCarData(state.carId);
                        }
                        break;

                    case 'category':
                        if (state.categoryId) {
                            setNavigationSource({ type: 'category', id: state.categoryId });
                            await fetchCategoryData(state.categoryId);
                        }
                        break;

                    case 'brand':
                        if (state.brandId) {
                            setNavigationSource({ type: 'brand', id: state.brandId });
                            await fetchBrandData(state.brandId);
                        }
                        break;

                    case 'vendor':
                        if (state.vendorId) {
                            setNavigationSource({ type: 'vendor', id: state.vendorId });
                            await fetchVendorData(state.vendorId);
                        }
                        break;

                    case 'price-list':
                        const priceListId = state.priceListItemId || state.pricelistItemId;
                        if (priceListId) {
                            setNavigationSource({ type: 'price-list', id: priceListId });
                            await fetchPriceListData(priceListId);
                        }
                        break;

                    default:
                        setNavigationSource({ type: 'inventory' });
                }
            } catch (error) {
                console.error('Error processing navigation state:', error);
                toast.error('Failed to load pre-filled data');
                setNavigationSource({ type: 'inventory' });
            } finally {
                setLoadingPreFill(false);
            }
        };

        checkNavigationState();
    }, [location.state, setValue]);

    // calculate purchase price
    const mrpValue = watch('mrp');
    const purchaseDiscountValue = watch('purchaseDiscount');
    const discountB2CValue = watch('discountB2C');
    const discountB2BValue = watch('discountB2B');

    useEffect(() => {
        // Convert to numbers, handle empty strings
        const mrpNum = mrpValue ? Number(mrpValue) : 0;
        const discountNum = purchaseDiscountValue ? Number(purchaseDiscountValue) : 0;
        const discountB2CNum = discountB2CValue ? Number(discountB2CValue) : 0;
        const discountB2BNum = discountB2BValue ? Number(discountB2BValue) : 0;

        // Only calculate if MRP is provided and valid
        if (mrpNum > 0) {
            const calculatedPrice = calculatePrice(mrpNum, discountNum);
            const calculatedB2CPrice = calculatePrice(mrpNum, discountB2CNum);
            const calculatedB2BPrice = calculatePrice(mrpNum, discountB2BNum);
            const calculatedUnitPrice = calculateUnitPrice(mrpNum);
            setValue('purchasePrice', calculatedPrice);
            setValue('sellingPriceB2C', calculatedB2CPrice);
            setValue('sellingPriceB2B', calculatedB2BPrice);
            setValue('unitPrice', calculatedUnitPrice);
        } else if (discountNum > 0 && !mrpValue) {
            setValue('purchasePrice', 0);
        } else if (discountB2CNum > 0 && !mrpValue) {
            setValue('sellingPriceB2C', 0);
        } else if (discountB2BNum > 0 && !mrpValue) {
            setValue('sellingPriceB2B', 0);
        }
    }, [calculatePrice, calculateUnitPrice, setValue, mrpValue, purchaseDiscountValue, discountB2CValue, discountB2BValue]);

    // Fetch all necessary data on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch categories
                const categoriesRes = await request({
                    url: 'category/get-all-category',
                    method: 'get',
                });
                if (categoriesRes.status === 'success') {
                    setCategories(categoriesRes.categories || []);
                }

                // Fetch all brands
                setLoadingBrands(true);
                const brandsRes = await request({
                    url: 'brand/get-all-brands',
                    method: 'get',
                });

                let brandsList: Brand[] = [];
                if (brandsRes.brands) {
                    brandsList = brandsRes.brands;
                } else if (brandsRes.data) {
                    brandsList = brandsRes.data;
                } else if (Array.isArray(brandsRes)) {
                    brandsList = brandsRes;
                }

                setAllBrands(brandsList);

                // Filter brands by manufactureType
                const carBrandsList = brandsList.filter(brand =>
                    brand.manufactureType.includes('Cars')
                );
                const partBrandsList = brandsList.filter(brand =>
                    brand.manufactureType.includes('Parts')
                );

                setCarBrands(carBrandsList);
                setPartBrands(partBrandsList);
                setLoadingBrands(false);

                // Fetch vendors
                setLoadingVendors(true);
                const vendorsRes = await request({
                    url: 'vendor/get-all-vendors',
                    method: 'get',
                });

                let vendorsList: Vendor[] = [];
                if (vendorsRes.vendors) {
                    vendorsList = vendorsRes.vendors;
                } else if (vendorsRes.data) {
                    vendorsList = vendorsRes.data;
                } else if (Array.isArray(vendorsRes)) {
                    vendorsList = vendorsRes;
                }

                setVendors(vendorsList || []);
                setLoadingVendors(false);

                // Fetch car models for compatibility
                setLoadingModels(true);
                const carModelsRes = await request({
                    url: 'compatibility/get-all-car-models',
                    method: 'get',
                });

                if (carModelsRes.models) {
                    const models = (carModelsRes.models || []) as CarModel[];
                    setCarModels(models);
                } else if (carModelsRes.data) {
                    const models = (carModelsRes.data || []) as CarModel[];
                    setCarModels(models);
                } else if (Array.isArray(carModelsRes)) {
                    setCarModels(carModelsRes);
                } else {
                    console.warn('Unexpected car models response structure:', carModelsRes);
                    setCarModels([]);
                }
                setLoadingModels(false);

            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load form data');
                setLoadingBrands(false);
                setLoadingVendors(false);
                setLoadingModels(false);
            }
        };

        fetchData();
    }, []);

    // Filter car models when brand is selected
    useEffect(() => {
        if (selectedCarBrand && carModels.length > 0) {
            const filtered = carModels.filter(model => {
                if (model.brand && model.brand._id === selectedCarBrand._id) {
                    return true;
                }
                if (typeof model.brand === 'string' && model.brand === selectedCarBrand._id) {
                    return true;
                }
                if (selectedCarBrand && model.brand && model.brand.name === selectedCarBrand.name) {
                    return true;
                }
                return false;
            });

            setFilteredCarModels(filtered);
            setSelectedCarModel(null);
        } else {
            setFilteredCarModels([]);
            setSelectedCarModel(null);
        }
    }, [selectedCarBrand, carModels]);

    // Reset compatibility selections when car model changes
    useEffect(() => {
        if (selectedCarModel) {
            setSelectedVariants([]);
            setSelectedFuelTypes([]);
            setSelectedTransmissions([]);
            setSelectedGenerations([]);
        }
    }, [selectedCarModel]);

    // Update attribute fields when category changes
    useEffect(() => {
        if (watchedCategory) {
            const category = categories.find(cat => cat.name === watchedCategory);
            setSelectedCategory(category || null);

            if (category && category.attributesTemplate) {
                const currentAttributes = watch('attributes') || {};
                const fields = category.attributesTemplate.map(attr => ({
                    ...attr,
                    value: currentAttributes[attr.key] !== undefined ? currentAttributes[attr.key] :
                        attr.type === 'boolean' ? false : attr.type === 'number' ? 0 : ''
                }));
                setAttributeFields(fields);
            } else {
                setAttributeFields([]);
            }
        } else {
            setSelectedCategory(null);
            setAttributeFields([]);
        }
    }, [watchedCategory, categories, watch]);

    const onSubmit = async (data: ProductFormData) => {
        try {
            setLoading(true);

            // Prepare attributes from form fields
            const attributes: Record<string, any> = {};
            attributeFields.forEach(field => {
                if (field.value !== '' && field.value !== null && field.value !== undefined) {
                    attributes[field.key] = field.value;
                }
            });

            // Prepare compatibility data according to carModelSchema
            const compatibilityData = selectedCompatibility.map(comp => {
                const selectedModel = carModels.find(model => model._id === comp.carModel);
                const generations = comp.generations.map(genStr => {
                    const [from, to] = genStr.split('-');
                    return {
                        from: from || '',
                        to: to || '',
                        images: []
                    };
                });

                return {
                    name: comp.carName,
                    brand: {
                        name: comp.brandName,
                        logo: selectedModel?.baseImage || comp.baseImage || '',
                        parentCompany: ''
                    },
                    baseImage: comp.baseImage || '',
                    variants: comp.variants,
                    fuelTypes: comp.fuelTypes,
                    transmissions: comp.transmissions,
                    generations: generations
                };
            });

            // Prepare description
            const jsonFieldsObj: Record<string, any> = {};
            descriptionJsonFields.forEach(field => {
                jsonFieldsObj[field.key] = field.value;
            })

            const descriptionData = {
                text: data.description?.text || "",
                jsonFields: jsonFieldsObj
            }

            // Prepare source metadata
            const metadataObj: Record<string, any> = {};
            sourceMetadata.forEach(field => {
                metadataObj[field.key] = field.value;
            });

            // Prepare source object
            const sourceData = {
                type: sourceType,
                id: sourceId || undefined,
                date: new Date(),
                metadata: metadataObj
            };

            const formData = new FormData();

            // Append basic fields
            formData.append('name', data.name);
            formData.append('partNo', data.partNo);
            formData.append('barcode', data.barcode || '');
            formData.append('quantity', data.quantity.toString());
            formData.append('category', data.category);
            formData.append('brand', data.brand);
            formData.append('vender', data.vender);
            formData.append('mrp', data.mrp.toString());
            formData.append('unitPrice', data.unitPrice.toString());
            formData.append('purchaseDiscount', data.purchaseDiscount.toString());
            formData.append('purchasePrice', data.purchasePrice.toString());
            formData.append('discountB2C', data.discountB2C.toString());
            formData.append('sellingPriceB2C', data.sellingPriceB2C.toString());
            formData.append('discountB2B', data.discountB2B.toString());
            formData.append('sellingPriceB2B', data.sellingPriceB2B.toString());
            formData.append('description', JSON.stringify(descriptionData));
            formData.append('status', data.status);

            // Append source and import data
            formData.append('source', JSON.stringify(sourceData));
            if (importBatchId.trim()) {
                formData.append('importBatchId', importBatchId.trim());
            }

            // formData.append('aliasNames', JSON.stringify(data.aliasNames || []));
            formData.append('compatibility', JSON.stringify(compatibilityData));
            formData.append('attributes', JSON.stringify(attributes));

            imageFiles.forEach(file => {
                formData.append('productImages', file);
            });

            const res = await request({
                url: 'product/add-product',
                method: 'post',
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (res.status === 'success') {
                toast.success(res.message);
                navigate('/inventory');
                reset();
                setSourceType('manual');
                setSourceId('');
                setImportBatchId('');
                setSourceMetadata([]);
                setNewSourceMetadata({ key: "", value: "" });
                setPriceListData(null);
                setDescriptionJsonFields([]);
                setNewJsonField({ key: "", value: "" });
            }
        } catch (error: any) {
            console.error('Error adding product:', error);
            toast.error(error);
        } finally {
            setLoading(false);
        }
    };

    // const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     const files = Array.from(event.target.files || []);
    //     if (files.length + imageFiles.length > 5) {
    //         toast.error('Maximum 5 images allowed');
    //         return;
    //     }

    //     setImageFiles(prev => [...prev, ...files]);

    //     // Create preview URLs
    //     files.forEach(file => {
    //         const reader = new FileReader();
    //         reader.onload = (e) => {
    //             setImagePreviews(prev => [...prev, e.target?.result as string]);
    //         };
    //         reader.readAsDataURL(file);
    //     });
    // };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        if (files.length + imageFiles.length > 5) {
            toast.error('Maximum 5 images allowed');
            return;
        }

        // Validate file sizes and types
        const invalidFiles = files.filter(f => f.size > 10 * 1024 * 1024);
        if (invalidFiles.length > 0) {
            toast.error('Some images exceed 10MB limit');
            return;
        }

        const invalidTypes = files.filter(f => !f.type.startsWith('image/'));
        if (invalidTypes.length > 0) {
            toast.error('Please upload only image files');
            return;
        }

        try {
            // Compress all images
            const compressedFiles = await compressMultipleImages(files);

            setImageFiles(prev => [...prev, ...compressedFiles]);

            // Create preview URLs for compressed images
            compressedFiles.forEach(file => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    setImagePreviews(prev => [...prev, e.target?.result as string]);
                };
                reader.readAsDataURL(file);
            });

            toast.success(`${compressedFiles.length} image(s) uploaded successfully`);
        } catch (error) {
            console.error('Compression error:', error);
            toast.error('Failed to compress images. Please try again.');

            // Fallback: add original files if compression fails
            setImageFiles(prev => [...prev, ...files]);
            files.forEach(file => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    setImagePreviews(prev => [...prev, e.target?.result as string]);
                };
                reader.readAsDataURL(file);
            });
        }

        // Reset input to allow re-uploading same files
        event.target.value = '';
    };

    const handleRemoveImage = (index: number) => {
        setImageFiles(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleAttributeChange = (index: number, value: any) => {
        const updatedFields = [...attributeFields];
        updatedFields[index].value = value;
        setAttributeFields(updatedFields);
    };

    const handleAddCompatibility = () => {
        if (!selectedCarModel) {
            toast.error('Please select a car model');
            return;
        }

        const brandName = selectedCarBrand?.name || (selectedCarModel.brand && selectedCarModel.brand.name) || 'Unknown Brand';

        // Convert generations to the string format that matches your display
        const generationStrings = selectedGenerations.map(gen => {
            const [from, to] = gen.split('-');
            return `${from}-${to}`;
        });

        const compatibility: SelectedCompatibility = {
            carModel: selectedCarModel._id,
            carName: selectedCarModel.name,
            brandName: brandName,
            baseImage: selectedCarModel.baseImage || '',
            variants: selectedVariants,
            fuelTypes: selectedFuelTypes,
            transmissions: selectedTransmissions,
            generations: generationStrings,
        };

        setSelectedCompatibility(prev => [...prev, compatibility]);

        // Reset all selections
        setSelectedCarBrand(null);
        setSelectedCarModel(null);
        setSelectedVariants([]);
        setSelectedFuelTypes([]);
        setSelectedTransmissions([]);
        setSelectedGenerations([]);
        setBrandSearchInput('');
        setModelSearchInput('');

        toast.success('Vehicle compatibility added successfully!');
    };

    const handleEditCompatibility = (index: number) => {
        const comp = selectedCompatibility[index];

        // Find the brand and model objects
        const brand = carBrands.find(b => b.name === comp.brandName);
        const model = carModels.find(m => m._id === comp.carModel);

        // Set the form fields with the existing compatibility data
        setSelectedCarBrand(brand || null);
        setSelectedCarModel(model || null);
        setSelectedVariants(comp.variants);
        setSelectedFuelTypes(comp.fuelTypes);
        setSelectedTransmissions(comp.transmissions);
        setSelectedGenerations(comp.generations);

        setEditingCompatibilityIndex(index);
        setEditDialogOpen(true);
    };

    const handleUpdateCompatibility = () => {
        if (!selectedCarModel || editingCompatibilityIndex === null) {
            toast.error('Please select a car model');
            return;
        }

        const brandName = selectedCarBrand?.name || (selectedCarModel.brand && selectedCarModel.brand.name) || 'Unknown Brand';

        // Convert generations to the string format that matches your display
        const generationStrings = selectedGenerations.map(gen => {
            const [from, to] = gen.split('-');
            return `${from}-${to}`;
        });

        const updatedCompatibility: SelectedCompatibility = {
            carModel: selectedCarModel._id,
            carName: selectedCarModel.name,
            brandName: brandName,
            baseImage: selectedCarModel.baseImage || '',
            variants: selectedVariants,
            fuelTypes: selectedFuelTypes,
            transmissions: selectedTransmissions,
            generations: generationStrings,
        };

        // Update the specific compatibility
        const updatedCompatibilities = [...selectedCompatibility];
        updatedCompatibilities[editingCompatibilityIndex] = updatedCompatibility;
        setSelectedCompatibility(updatedCompatibilities);

        // Reset all selections and close dialog
        setSelectedCarBrand(null);
        setSelectedCarModel(null);
        setSelectedVariants([]);
        setSelectedFuelTypes([]);
        setSelectedTransmissions([]);
        setSelectedGenerations([]);
        setEditDialogOpen(false);
        setEditingCompatibilityIndex(null);

        toast.success('Vehicle compatibility updated successfully!');
    };

    const handleRemoveCompatibility = (index: number) => {
        setSelectedCompatibility(prev => prev.filter((_, i) => i !== index));
    };

    // Filter functions for Autocomplete
    const filterBrands = (options: Brand[], { inputValue }: { inputValue: string }) => {
        return options.filter(option =>
            option.name.toLowerCase().includes(inputValue.toLowerCase())
        );
    };

    const filterModels = (options: CarModel[], { inputValue }: { inputValue: string }) => {
        return options.filter(option =>
            option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
            (option.brand && option.brand.name.toLowerCase().includes(inputValue.toLowerCase()))
        );
    };

    const filterCategories = (options: Category[], { inputValue }: { inputValue: string }) => {
        return options.filter(option =>
            option.name.toLowerCase().includes(inputValue.toLowerCase())
        );
    };

    const filterVendors = (options: Vendor[], { inputValue }: { inputValue: string }) => {
        return options.filter(option =>
            option.vendorName.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.nickName.toLowerCase().includes(inputValue.toLowerCase())
        );
    };

    // Prevent negative numbers in input fields
    const preventNegativeNumbers = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === '-' || e.key === 'e' || e.key === 'E') {
            e.preventDefault();
        }
    };

    const renderAttributeField = (field: AttributeField, index: number) => {
        switch (field.type) {
            case 'string':
                return (
                    <TextField
                        size="small"
                        label={field.label}
                        fullWidth
                        value={field.value || ''}
                        onChange={(e) => handleAttributeChange(index, e.target.value)}
                        required={field.required}
                    />
                );
            case 'number':
                return (
                    <TextField
                        size="small"
                        label={field.label}
                        type="number"
                        fullWidth
                        value={field.value || 0}
                        onChange={(e) => handleAttributeChange(index, Math.max(0, parseFloat(e.target.value) || 0))}
                        onKeyDown={preventNegativeNumbers}
                        inputProps={{ min: 0 }}
                        required={field.required}
                    />
                );
            case 'select':
                return (
                    <FormControl fullWidth size="small">
                        <InputLabel>{field.label}</InputLabel>
                        <Select
                            value={field.value || ''}
                            label={field.label}
                            onChange={(e) => handleAttributeChange(index, e.target.value)}
                            required={field.required}
                        >
                            {field.options.map(option => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            case 'boolean':
                return (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={field.value || false}
                                onChange={(e) => handleAttributeChange(index, e.target.checked)}
                            />
                        }
                        label={field.label}
                    />
                );
            case 'date':
                return (
                    <TextField
                        size="small"
                        label={field.label}
                        type="date"
                        fullWidth
                        value={field.value || ''}
                        onChange={(e) => handleAttributeChange(index, e.target.value)}
                        required={field.required}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                );
            default:
                return null;
        }
    };

    // Get pre-fill indicator text and color
    const getPreFillInfo = () => {
        switch (navigationSource.type) {
            case 'car':
                return { text: 'Car Compatibility Pre-filled', color: 'success' as const };
            case 'category':
                return { text: 'Category Pre-filled', color: 'primary' as const };
            case 'brand':
                return { text: 'Brand Pre-filled', color: 'secondary' as const };
            case 'vendor':
                return { text: 'Vendor Pre-filled', color: 'warning' as const };
            case 'price-list':
                return { text: 'Price List Pre-filled', color: 'info' as const };
            default:
                return null;
        }
    };

    const preFillInfo = getPreFillInfo();

    // Show loading state while fetching pre-fill data
    if (loadingPreFill) {
        return (
            <Card elevation={2} sx={{ borderRadius: 1, maxWidth: 1200, mx: 'auto' }}>
                <CardContent sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <CircularProgress />
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            {navigationSource.type === 'price-list' ? 'Loading price list data...' : 'Loading pre-filled data...'}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Box sx={{ mb: 2 }}>
                <Brandcrumb />
            </Box>
            <Card elevation={2} sx={{ borderRadius: 1, width: "100%", mx: 'auto' }} >
                <CardContent sx={{ p: 3 }}>
                    {/* Header Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" fontWeight={600} color="primary" gutterBottom>
                            Add New Product
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Add a new product to your inventory
                        </Typography>

                        {/* Show pre-fill indicator */}
                        {preFillInfo && (
                            <Box sx={{ mt: 1 }}>
                                <Chip
                                    label={preFillInfo.text}
                                    color={preFillInfo.color}
                                    variant="outlined"
                                    size="small"
                                    icon={
                                        navigationSource.type === 'car' ? <DirectionsCarIcon /> :
                                            navigationSource.type === 'category' ? <CategoryIcon /> :
                                                navigationSource.type === 'brand' ? <BrandingWatermarkIcon /> :
                                                    navigationSource.type === 'vendor' ? <BusinessIcon /> :
                                                        navigationSource.type === 'price-list' ? <InventoryIcon /> :
                                                            <InventoryIcon />
                                    }
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Price List Import Summary */}
                    {priceListData && (
                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#e3f2fd', mb: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <InventoryIcon color="info" fontSize="small" />
                                <Typography variant="subtitle1" fontWeight={600} color="info.main">
                                    Price List Import
                                </Typography>
                                <Chip
                                    label="Auto-filled"
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                />
                            </Stack>

                            <Stack spacing={2}>
                                <Alert severity="info" icon={<InfoIcon />}>
                                    <Typography variant="body2">
                                        <strong>Note:</strong> Product Name, Part No, Vendor, and MRP are auto-filled from the price list.
                                        All other price list fields ({descriptionJsonFields.length}) have been added to the description section below.
                                    </Typography>
                                </Alert>

                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 6, md: 3 }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Source ID
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                                {priceListData._id}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {priceListData.sourceFile && (
                                        <Grid size={{ xs: 6, md: 3 }}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Source File
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {priceListData.sourceFile}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    )}

                                    {priceListData.importBatchId && (
                                        <Grid size={{ xs: 6, md: 3 }}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Import Batch
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {priceListData.importBatchId.substring(0, 8)}...
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            </Stack>
                        </Paper>
                    )}

                    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                        <Stack spacing={3}>
                            {/* Rest of your existing form sections remain exactly the same */}
                            {/* Basic Information Section with Images */}
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <InventoryIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Basic Information
                                    </Typography>
                                </Stack>

                                <Stack spacing={2}>
                                    {/* Product Images */}
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                            Product Images
                                        </Typography>
                                        <Stack direction="row" spacing={2} alignItems="flex-start">
                                            <Box sx={{ flex: 1 }}>
                                                <Button
                                                    variant="outlined"
                                                    component="label"
                                                    startIcon={<CloudUploadIcon />}
                                                    size="small"
                                                    fullWidth
                                                    sx={{ mb: 1 }}
                                                    disabled={isCompressing || imageFiles.length >= 5}
                                                >
                                                    {isCompressing ? 'Compressing...' : 'Upload Images'}
                                                    <input
                                                        type="file"
                                                        hidden
                                                        multiple
                                                        accept="image/*"
                                                        onChange={handleImageUpload}
                                                        disabled={imageFiles.length >= 5}
                                                    />
                                                </Button>
                                                <Typography variant="caption" color="text.secondary">
                                                    Max 5 images • JPG, PNG, WebP
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ flex: 1 }}>
                                                {imagePreviews.map((preview, index) => (
                                                    <Box key={index} sx={{ position: 'relative', mb: 1 }}>
                                                        <Avatar
                                                            variant="rounded"
                                                            src={preview}
                                                            sx={{ width: 60, height: 60 }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: -8,
                                                                right: -8,
                                                                backgroundColor: 'error.main',
                                                                color: 'white',
                                                                width: 20,
                                                                height: 20,
                                                                '&:hover': {
                                                                    backgroundColor: 'error.dark',
                                                                },
                                                            }}
                                                            onClick={() => handleRemoveImage(index)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Stack>
                                    </Box>

                                    {/* Product Details */}
                                    <Stack spacing={2}>
                                        <Stack direction="row" spacing={2}>
                                            <TextField
                                                size="small"
                                                label="Product Name *"
                                                placeholder="Enter product name"
                                                fullWidth
                                                error={!!errors.name}
                                                helperText={errors.name?.message}
                                                {...register('name', {
                                                    required: 'Product name is required',
                                                    minLength: { value: 2, message: 'Product name must be at least 2 characters' }
                                                })}
                                            />
                                            <TextField
                                                size="small"
                                                label="Part No *"
                                                placeholder="Enter Part No"
                                                fullWidth
                                                error={!!errors.partNo}
                                                helperText={errors.partNo?.message}
                                                {...register('partNo', {
                                                    required: 'Part No is required',
                                                    pattern: {
                                                        value: /^[A-Z0-9_-]+$/,
                                                        message: 'Part No must contain only uppercase letters, numbers, hyphens, and underscores'
                                                    }
                                                })}
                                            />
                                        </Stack>

                                        <Stack direction="row" spacing={2}>
                                            <TextField
                                                size="small"
                                                label="Barcode"
                                                placeholder="Enter barcode"
                                                fullWidth
                                                error={!!errors.barcode}
                                                helperText={errors.barcode?.message}
                                                {...register('barcode')}
                                            />
                                            <TextField
                                                size="small"
                                                label="Quantity *"
                                                type="number"
                                                fullWidth
                                                error={!!errors.quantity}
                                                helperText={errors.quantity?.message}
                                                onKeyDown={preventNegativeNumbers}
                                                inputProps={{ min: 0 }}
                                                {...register('quantity', {
                                                    required: 'Quantity is required',
                                                    min: { value: 0, message: 'Quantity cannot be negative' },
                                                    valueAsNumber: true,
                                                })}
                                            />
                                        </Stack>

                                        {/* Description Section */}
                                        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                                <DescriptionIcon color="primary" fontSize="small" />
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    Description
                                                </Typography>
                                                {priceListData && (
                                                    <Chip
                                                        label={`${descriptionJsonFields.length} fields from price list`}
                                                        size="small"
                                                        color="info"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Stack>

                                            <Stack spacing={2}>
                                                {/* Description Text */}
                                                {/* Description Text */}
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                                        Description Text
                                                    </Typography>
                                                    <Controller
                                                        name="description.text"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                size="small"
                                                                label="Description"
                                                                placeholder="Product description"
                                                                multiline
                                                                rows={3}
                                                                fullWidth
                                                                {...field}
                                                                value={field.value || ''}
                                                            />
                                                        )}
                                                    />
                                                </Box>

                                                {/* JSON Fields Section */}
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                                        Additional Information (Key-Value Pairs)
                                                    </Typography>
                                                    {priceListData && (
                                                        <Alert severity="info" sx={{ mb: 2 }}>
                                                            <Typography variant="caption">
                                                                Price list fields are shown below. You can edit or remove them.
                                                            </Typography>
                                                        </Alert>
                                                    )}

                                                    {/* Add new field */}
                                                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                                        <TextField
                                                            size="small"
                                                            placeholder="Key (e.g., weight, material)"
                                                            value={newJsonField.key}
                                                            onChange={(e) => setNewJsonField({ ...newJsonField, key: e.target.value })}
                                                            sx={{ flex: 1 }}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            placeholder="Value"
                                                            value={newJsonField.value}
                                                            onChange={(e) => setNewJsonField({ ...newJsonField, value: e.target.value })}
                                                            sx={{ flex: 1 }}
                                                        />
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => {
                                                                if (newJsonField.key.trim() && newJsonField.value.trim()) {
                                                                    setDescriptionJsonFields([...descriptionJsonFields, newJsonField]);
                                                                    setNewJsonField({ key: '', value: '' });
                                                                }
                                                            }}
                                                            disabled={!newJsonField.key.trim() || !newJsonField.value.trim()}
                                                        >
                                                            Add
                                                        </Button>
                                                    </Stack>

                                                    {/* Display added fields */}
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                                        {descriptionJsonFields.map((field, index) => (
                                                            <Chip
                                                                key={index}
                                                                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Typography variant="caption" >
                                                                        {field.key}:
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {field.value.length > 15 ? `${field.value.substring(0, 15)}...` : field.value}
                                                                    </Typography>
                                                                </Box>}
                                                                onDelete={() => {
                                                                    const updated = [...descriptionJsonFields];
                                                                    updated.splice(index, 1);
                                                                    setDescriptionJsonFields(updated);
                                                                }}
                                                                color="primary"
                                                                variant="outlined"
                                                                size="small"
                                                                sx={{
                                                                    '& .MuiChip-label': {
                                                                        fontSize: '0.75rem',
                                                                        px: 1,
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </Stack>
                                        </Paper>
                                    </Stack>
                                </Stack>
                            </Paper>

                            {/* Brand & Vendor Section */}
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <BrandingWatermarkIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Brand & Vendor
                                    </Typography>
                                </Stack>

                                <Stack spacing={2} direction="row" justifyContent="space-between">
                                    <Box minWidth="50%">
                                        <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                            Product Brand *
                                        </Typography>
                                        <Controller
                                            name="brand"
                                            control={control}
                                            rules={{ required: 'Brand is required' }}
                                            render={({ field }) => (
                                                <Autocomplete
                                                    size="small"
                                                    options={partBrands}
                                                    getOptionLabel={(option) => option.name}
                                                    filterOptions={filterBrands}
                                                    value={partBrands.find(brand => brand.name === field.value) || null}
                                                    onChange={(event, newValue) => {
                                                        field.onChange(newValue ? newValue.name : '');
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Select Product Brand"
                                                            placeholder="Type to search brands..."
                                                            error={!!errors.brand}
                                                            helperText={errors.brand?.message}
                                                            disabled={loadingBrands}
                                                        />
                                                    )}
                                                    loading={loadingBrands}
                                                    noOptionsText="No brands found"
                                                />
                                            )}
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            Brands with manufacturing type: Parts
                                        </Typography>
                                        {priceListData && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            <i>Suggestion from Price List</i> : <b>{priceListData.productBrand ? priceListData.productBrand : "No Brand Spacified"} </b>
                                        </Typography>
                                        }
                                    </Box>

                                    <Box minWidth="50%">
                                        <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                            Vendor *
                                        </Typography>
                                        <Controller
                                            name="vender"
                                            control={control}
                                            rules={{ required: 'Vendor is required' }}
                                            render={({ field }) => (
                                                <Autocomplete
                                                    size="small"
                                                    options={vendors}
                                                    getOptionLabel={(option) => option.vendorName}
                                                    filterOptions={filterVendors}
                                                    value={vendors.find(vendor => vendor.vendorName === field.value) || null}
                                                    onChange={(event, newValue) => {
                                                        field.onChange(newValue ? newValue.vendorName : '');
                                                    }}
                                                    inputValue={vendorSearchInput}
                                                    onInputChange={(event, newInputValue) => {
                                                        setVendorSearchInput(newInputValue);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Select Vendor"
                                                            placeholder="Type to search vendors..."
                                                            error={!!errors.vender}
                                                            helperText={errors.vender?.message}
                                                            disabled={loadingVendors}
                                                        />
                                                    )}
                                                    loading={loadingVendors}
                                                    noOptionsText="No vendors found"
                                                    renderOption={(props, option) => (
                                                        <li {...props}>
                                                            <Box>
                                                                <Typography variant="body2">
                                                                    {option.vendorName}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {option.nickName} • {option.contact.email}
                                                                </Typography>
                                                            </Box>
                                                        </li>
                                                    )}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Stack>
                            </Paper>

                            {/* Pricing Section */}
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <CurrencyRupeeIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Pricing Information (₹)
                                    </Typography>
                                </Stack>

                                <Stack direction="column" justifyContent="space-between" spacing={2}>
                                    <Box sx={{display:"flex", gap:2, justifyContent:'space-between'}}>
                                        {/* MRP Price Field */}
                                        <TextField
                                            size="small"
                                            label="MRP *"
                                            type="number"
                                            fullWidth
                                            error={!!errors.mrp}
                                            helperText={errors.mrp?.message}
                                            onKeyDown={preventNegativeNumbers}
                                            inputProps={{ min: 0, step: "0.01" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CurrencyRupeeIcon color="action" fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...register('mrp', {
                                                required: 'MRP is required',
                                                min: { value: 0, message: 'MRP cannot be negative' },
                                                valueAsNumber: true,
                                            })}
                                        />

                                        {/* Unit Price Field */}
                                        <TextField
                                            size="small"
                                            label="Unit Price *"
                                            type="number"
                                            fullWidth
                                            error={!!errors.unitPrice}
                                            helperText={errors.unitPrice?.message}
                                            onKeyDown={preventNegativeNumbers}
                                            inputProps={{ min: 0, step: "0.01" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CurrencyRupeeIcon color="action" fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...register('unitPrice', {
                                                required: 'Unit Price is required. only 2 digits allowed after decimal',
                                                min: { value: 0, message: 'Unit Price cannot be negative' },
                                                valueAsNumber: true,
                                            })}
                                        />
                                    </Box>
                                    <Box sx={{display:"flex", gap:2, justifyContent:'space-between'}}>
                                        {/* Purchase discount percentage Field */}
                                        <TextField
                                            size="small"
                                            label="Purchase Discount (%) *"
                                            type="number"
                                            fullWidth
                                            error={!!errors.purchaseDiscount}
                                            helperText={errors.purchaseDiscount?.message}
                                            onKeyDown={preventNegativeNumbers}
                                            inputProps={{ min: 0, step: "0.01" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PercentIcon color="action" fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...register('purchaseDiscount', {
                                                required: 'purchaseDiscount is required',
                                                min: { value: 0, message: 'purchaseDiscount cannot be negative' },
                                                valueAsNumber: true,
                                            })}
                                        />

                                        {/* Purchase Price Field */}
                                        <Box sx={{ width:'100%' }}>
                                            <TextField
                                                size="small"
                                                label="Purchase Price *"
                                                type="number"
                                                fullWidth
                                                error={!!errors.purchasePrice}
                                                helperText={errors.purchasePrice?.message}
                                                onKeyDown={preventNegativeNumbers}
                                                inputProps={{ min: 0, step: "0.01" }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <CurrencyRupeeIcon color="action" fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                {...register('purchasePrice', {
                                                    required: 'Purchase price is required',
                                                    min: { value: 0, message: 'Purchase price cannot be negative' },
                                                    valueAsNumber: true,
                                                })}
                                            />
                                            <Chip label={<PriceCodeDisplay number={watch('purchasePrice')} />} />
                                        </Box>
                                    </Box>
                                    <Box sx={{display:"flex", gap:2, justifyContent:'space-between'}}>
                                        {/* B2C discount percentage Field */}
                                        <TextField
                                            size="small"
                                            label="B2C Discount (%) *"
                                            type="number"
                                            fullWidth
                                            error={!!errors.discountB2C}
                                            helperText={errors.discountB2C?.message}
                                            onKeyDown={preventNegativeNumbers}
                                            inputProps={{ min: 0, step: "0.01" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PercentIcon color="action" fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...register('discountB2C', {
                                                required: 'discountB2C is required',
                                                min: { value: 0, message: 'discountB2C cannot be negative' },
                                                valueAsNumber: true,
                                            })}
                                        />

                                        <TextField
                                            size="small"
                                            label="Selling Price B2C *"
                                            type="number"
                                            fullWidth
                                            error={!!errors.sellingPriceB2C}
                                            helperText={errors.sellingPriceB2C?.message}
                                            onKeyDown={preventNegativeNumbers}
                                            inputProps={{ min: 0, step: "0.01" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CurrencyRupeeIcon color="action" fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...register('sellingPriceB2C', {
                                                required: 'Selling price B2C is required',
                                                min: { value: 0, message: 'Selling price B2C cannot be negative' },
                                                valueAsNumber: true,
                                            })}
                                        />
                                    </Box>
                                    <Box sx={{display:"flex", gap:2, justifyContent:'space-between'}}>
                                        {/* B2B discount percentage Field */}
                                        <TextField
                                            size="small"
                                            label="B2B Discount (%) *"
                                            type="number"
                                            fullWidth
                                            error={!!errors.discountB2B}
                                            helperText={errors.discountB2B?.message}
                                            onKeyDown={preventNegativeNumbers}
                                            inputProps={{ min: 0, step: "0.01" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PercentIcon color="action" fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...register('discountB2B', {
                                                required: 'discountB2B is required',
                                                min: { value: 0, message: 'discountB2B cannot be negative' },
                                                valueAsNumber: true,
                                            })}
                                        />

                                        <TextField
                                            size="small"
                                            label="Selling Price B2B *"
                                            type="number"
                                            fullWidth
                                            error={!!errors.sellingPriceB2B}
                                            helperText={errors.sellingPriceB2B?.message}
                                            onKeyDown={preventNegativeNumbers}
                                            inputProps={{ min: 0, step: "0.01" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CurrencyRupeeIcon color="action" fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...register('sellingPriceB2B', {
                                                required: 'Selling price B2B is required',
                                                min: { value: 0, message: 'Selling price B2B cannot be negative' },
                                                valueAsNumber: true,
                                            })}
                                        />
                                    </Box>
                                </Stack>
                            </Paper>

                            {/* Rest of your existing sections remain exactly the same */}
                            {/* Vehicle Compatibility Section */}
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <DirectionsCarIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Vehicle Compatibility
                                    </Typography>
                                </Stack>

                                <Stack spacing={3}>
                                    {/* Brand and Model Selection with Autocomplete */}
                                    <Stack direction="row" spacing={2}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                                Select Car Brand
                                            </Typography>
                                            <Autocomplete
                                                size="small"
                                                options={carBrands}
                                                getOptionLabel={(option) => option.name}
                                                filterOptions={filterBrands}
                                                value={selectedCarBrand}
                                                onChange={(event, newValue) => {
                                                    setSelectedCarBrand(newValue);
                                                }}
                                                inputValue={brandSearchInput}
                                                onInputChange={(event, newInputValue) => {
                                                    setBrandSearchInput(newInputValue);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Search Car Brand"
                                                        placeholder="Type to search car brands..."
                                                        disabled={loadingBrands}
                                                    />
                                                )}
                                                loading={loadingBrands}
                                                noOptionsText="No car brands found"
                                            />
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                Brands with manufacturing type: Cars
                                            </Typography>
                                            {priceListData && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                <i>Suggestion from Price List</i> : <b>{priceListData.carBrand}</b>
                                            </Typography>}
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                                Select Car Model
                                            </Typography>
                                            <Autocomplete
                                                size="small"
                                                options={filteredCarModels}
                                                getOptionLabel={(option) => option.name}
                                                filterOptions={filterModels}
                                                value={selectedCarModel}
                                                onChange={(event, newValue) => {
                                                    setSelectedCarModel(newValue);
                                                }}
                                                inputValue={modelSearchInput}
                                                onInputChange={(event, newInputValue) => {
                                                    setModelSearchInput(newInputValue);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Search Car Model"
                                                        placeholder="Type to search models..."
                                                        disabled={loadingModels || filteredCarModels.length === 0}
                                                    />
                                                )}
                                                loading={loadingModels}
                                                noOptionsText={selectedCarBrand ? "No models found for this brand" : "Select a car brand first"}
                                                renderOption={(props, option) => (
                                                    <li {...props}>
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {option.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {option.brand?.name}
                                                            </Typography>
                                                        </Box>
                                                    </li>
                                                )}
                                            />
                                            {priceListData && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                <i>Suggestion from Price List</i> : <b>{priceListData.carModel}</b>
                                            </Typography>}
                                        </Box>
                                    </Stack>

                                    {/* Car Details */}
                                    {selectedCarModel && (
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 2 }}>
                                                Configure Compatibility
                                            </Typography>

                                            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    {selectedCarModel.baseImage && (
                                                        <Avatar
                                                            variant="rounded"
                                                            src={(baseUrlImage + selectedCarModel.baseImage)}
                                                            sx={{ width: 60, height: 45 }}
                                                        />
                                                    )}
                                                    <Box>
                                                        <Typography variant="body1" fontWeight={600}>
                                                            {selectedCarModel.brand?.name} {selectedCarModel.name}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Paper>

                                            <Stack spacing={2}>
                                                <Stack direction="row" spacing={2}>
                                                    {selectedCarModel.variants.length > 0 && (
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Variants</InputLabel>
                                                            <Select
                                                                multiple
                                                                value={selectedVariants}
                                                                label="Variants"
                                                                onChange={(e) => setSelectedVariants(e.target.value as string[])}
                                                                renderValue={(selected) => (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                        {selected.slice(0, 2).map((value) => (
                                                                            <Chip key={value} label={value} size="small" />
                                                                        ))}
                                                                        {selected.length > 2 && (
                                                                            <Chip label={`+${selected.length - 2}`} size="small" />
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            >
                                                                {selectedCarModel.variants.map(variant => (
                                                                    <MenuItem key={variant} value={variant}>
                                                                        <Checkbox checked={selectedVariants.indexOf(variant) > -1} />
                                                                        {variant}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                    {selectedCarModel.fuelTypes.length > 0 && (
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Fuel Types</InputLabel>
                                                            <Select
                                                                multiple
                                                                value={selectedFuelTypes}
                                                                label="Fuel Types"
                                                                onChange={(e) => setSelectedFuelTypes(e.target.value as string[])}
                                                                renderValue={(selected) => (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                        {selected.map((value) => (
                                                                            <Chip key={value} label={value} size="small" />
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                            >
                                                                {selectedCarModel.fuelTypes.map(fuelType => (
                                                                    <MenuItem key={fuelType} value={fuelType}>
                                                                        <Checkbox checked={selectedFuelTypes.indexOf(fuelType) > -1} />
                                                                        {fuelType}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </Stack>
                                                <Stack direction="row" spacing={2}>
                                                    {selectedCarModel.transmissions.length > 0 && (
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Transmissions</InputLabel>
                                                            <Select
                                                                multiple
                                                                value={selectedTransmissions}
                                                                label="Transmissions"
                                                                onChange={(e) => setSelectedTransmissions(e.target.value as string[])}
                                                                renderValue={(selected) => (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                        {selected.map((value) => (
                                                                            <Chip key={value} label={value} size="small" />
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                            >
                                                                {selectedCarModel.transmissions.map(transmission => (
                                                                    <MenuItem key={transmission} value={transmission}>
                                                                        <Checkbox checked={selectedTransmissions.indexOf(transmission) > -1} />
                                                                        {transmission}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                    {selectedCarModel.generations.length > 0 && (
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel>Generations</InputLabel>
                                                            <Select
                                                                multiple
                                                                value={selectedGenerations}
                                                                label="Generations"
                                                                onChange={(e) => setSelectedGenerations(e.target.value as string[])}
                                                                renderValue={(selected) => (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                        {selected.slice(0, 2).map((value) => (
                                                                            <Chip key={value} label={value} size="small" />
                                                                        ))}
                                                                        {selected.length > 2 && (
                                                                            <Chip label={`+${selected.length - 2}`} size="small" />
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            >
                                                                {selectedCarModel.generations.map((gen, index) => (
                                                                    <MenuItem key={index} value={`${gen.from}-${gen.to}`}>
                                                                        <Checkbox checked={selectedGenerations.indexOf(`${gen.from}-${gen.to}`) > -1} />
                                                                        {gen.from} - {gen.to}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </Stack>
                                            </Stack>

                                            <Box sx={{ pt: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<AddIcon />}
                                                    onClick={handleAddCompatibility}
                                                    disabled={!selectedCarModel}
                                                    fullWidth
                                                    size="small"
                                                >
                                                    Add Vehicle Compatibility
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Selected Compatibilities */}
                                    {selectedCompatibility.length > 0 && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 2 }}>
                                                Added Compatibilities ({selectedCompatibility.length})
                                            </Typography>
                                            <Stack spacing={2}>
                                                {selectedCompatibility.map((comp, index) => (
                                                    <Paper
                                                        key={index}
                                                        variant="outlined"
                                                        sx={{
                                                            p: 2,
                                                            position: 'relative',
                                                            borderColor: 'success.light',
                                                            bgcolor: 'success.light + 0.04'
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={2} alignItems="flex-start">
                                                            {comp.baseImage && (
                                                                <Avatar
                                                                    variant="rounded"
                                                                    src={baseUrlImage + comp.baseImage}
                                                                    sx={{ width: 50, height: 38 }}
                                                                />
                                                            )}
                                                            <Box sx={{ flex: 1 }}>
                                                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                                    <Box>
                                                                        <Typography variant="body1" fontWeight={600} fontSize="0.9rem">
                                                                            {comp.brandName} {comp.carName}
                                                                        </Typography>

                                                                        {/* Detailed Compatibility Information */}
                                                                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                                                                            {comp.variants.length > 0 && (
                                                                                <Box>
                                                                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                                                        Variants:
                                                                                    </Typography>
                                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                                                                                        {comp.variants.map((variant, i) => (
                                                                                            <Chip
                                                                                                key={i}
                                                                                                label={variant}
                                                                                                size="small"
                                                                                                color="primary"
                                                                                                variant="outlined"
                                                                                            />
                                                                                        ))}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}

                                                                            {comp.fuelTypes.length > 0 && (
                                                                                <Box>
                                                                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                                                        Fuel Types:
                                                                                    </Typography>
                                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                                                                                        {comp.fuelTypes.map((fuelType, i) => (
                                                                                            <Chip
                                                                                                key={i}
                                                                                                label={fuelType}
                                                                                                size="small"
                                                                                                color="success"
                                                                                                variant="outlined"
                                                                                            />
                                                                                        ))}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}

                                                                            {comp.transmissions.length > 0 && (
                                                                                <Box>
                                                                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                                                        Transmissions:
                                                                                    </Typography>
                                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                                                                                        {comp.transmissions.map((transmission, i) => (
                                                                                            <Chip
                                                                                                key={i}
                                                                                                label={transmission}
                                                                                                size="small"
                                                                                                color="info"
                                                                                                variant="outlined"
                                                                                            />
                                                                                        ))}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}

                                                                            {comp.generations.length > 0 && (
                                                                                <Box>
                                                                                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                                                        Generations:
                                                                                    </Typography>
                                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                                                                                        {comp.generations.map((generation, i) => (
                                                                                            <Chip
                                                                                                key={i}
                                                                                                label={generation}
                                                                                                size="small"
                                                                                                color="warning"
                                                                                                variant="outlined"
                                                                                            />
                                                                                        ))}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}
                                                                        </Stack>
                                                                    </Box>
                                                                    <Stack direction="row" spacing={0.5}>
                                                                        <Tooltip title="Edit compatibility">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleEditCompatibility(index)}
                                                                                sx={{
                                                                                    color: 'primary.main',
                                                                                    '&:hover': { backgroundColor: 'primary.light' }
                                                                                }}
                                                                            >
                                                                                <EditIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Remove compatibility">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleRemoveCompatibility(index)}
                                                                                sx={{
                                                                                    color: 'error.main',
                                                                                    '&:hover': { backgroundColor: 'error.light' }
                                                                                }}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Stack>
                                                                </Stack>
                                                            </Box>
                                                        </Stack>
                                                    </Paper>
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}

                                    {/* Empty State */}
                                    {selectedCompatibility.length === 0 && !selectedCarBrand && navigationSource.type !== 'car' && (
                                        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                                            <DirectionsCarIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
                                            <Typography variant="h6" color="text.secondary" gutterBottom fontSize="1rem">
                                                No Vehicle Compatibility Added
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                                                Start by selecting a car brand to add vehicle compatibility
                                            </Typography>
                                        </Paper>
                                    )}
                                </Stack>
                            </Paper>

                            {/* Category & Status Section */}
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <CategoryIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Category & Status
                                    </Typography>
                                </Stack>

                                <Stack direction="row" spacing={2}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                            Select Category
                                        </Typography>
                                        <Controller
                                            name="category"
                                            control={control}
                                            rules={{ required: 'Category is required' }}
                                            render={({ field }) => (
                                                <Autocomplete
                                                    size="small"
                                                    options={categories}
                                                    getOptionLabel={(option) => option.name}
                                                    filterOptions={filterCategories}
                                                    value={categories.find(cat => cat.name === field.value) || null}
                                                    onChange={(event, newValue) => {
                                                        field.onChange(newValue ? newValue.name : '');
                                                    }}
                                                    inputValue={categorySearchInput}
                                                    onInputChange={(event, newInputValue) => {
                                                        setCategorySearchInput(newInputValue);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Search Category"
                                                            placeholder="Type to search categories..."
                                                            error={!!errors.category}
                                                            helperText={errors.category?.message}
                                                        />
                                                    )}
                                                    noOptionsText="No categories found"
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                            Select Status
                                        </Typography>
                                        <FormControl size="small" fullWidth>
                                            <InputLabel>Status</InputLabel>
                                            <Controller
                                                name="status"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select {...field} label="Status">
                                                        <MenuItem value="active">Active</MenuItem>
                                                        <MenuItem value="inactive">Inactive</MenuItem>
                                                    </Select>
                                                )}
                                            />
                                        </FormControl>
                                    </Box>
                                </Stack>
                            </Paper>

                            {/* Category-specific Attributes */}
                            {attributeFields.length > 0 && (
                                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                        <CategoryIcon color="primary" fontSize="small" />
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {selectedCategory?.name} Attributes
                                        </Typography>
                                    </Stack>

                                    <Stack spacing={2}>
                                        {attributeFields.map((field, index) => (
                                            <Box key={field.key}>
                                                {renderAttributeField(field, index)}
                                            </Box>
                                        ))}
                                    </Stack>
                                </Paper>
                            )}

                            {/* Source & Import Information Section */}
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: '#f3f5f8' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <InventoryIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Source & Import Information
                                    </Typography>
                                    {priceListData && (
                                        <Chip
                                            label="Auto-set from price list"
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                        />
                                    )}
                                </Stack>

                                <Stack spacing={2}>
                                    <Stack direction="row" spacing={2}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                                Source Type *
                                            </Typography>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Source Type</InputLabel>
                                                <Select
                                                    value={sourceType}
                                                    label="Source Type"
                                                    onChange={(e) => setSourceType(e.target.value as any)}
                                                    disabled={!!priceListData}
                                                >
                                                    <MenuItem value="manual">Manual</MenuItem>
                                                    <MenuItem value="price-list">Price List</MenuItem>
                                                    <MenuItem value="import">Import</MenuItem>
                                                    <MenuItem value="oem">OEM</MenuItem>
                                                    <MenuItem value="oes">OES</MenuItem>
                                                    <MenuItem value="lot">LOT</MenuItem>
                                                </Select>
                                            </FormControl>
                                            {priceListData && (
                                                <Typography variant="caption" color="info.main" sx={{ mt: 0.5, display: 'block' }}>
                                                    Auto-set to "price-list"
                                                </Typography>
                                            )}
                                        </Box>

                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                                Source ID
                                            </Typography>
                                            <TextField
                                                size="small"
                                                label="Source ID"
                                                placeholder="Enter source ID"
                                                fullWidth
                                                value={sourceId}
                                                onChange={(e) => setSourceId(e.target.value)}
                                                InputProps={{
                                                    readOnly: !!priceListData,
                                                }}
                                                helperText={priceListData ? "Auto-filled from price list" : ""}
                                            />
                                        </Box>

                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                                Import Batch ID
                                            </Typography>
                                            <TextField
                                                size="small"
                                                label="Import Batch ID"
                                                placeholder="Enter import batch ID"
                                                fullWidth
                                                value={importBatchId}
                                                onChange={(e) => setImportBatchId(e.target.value)}
                                            />
                                        </Box>
                                    </Stack>

                                    {/* Source Metadata Section */}
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                                            Source Metadata (Key-Value Pairs)
                                        </Typography>
                                        {priceListData && (
                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                <Typography variant="caption">
                                                    Price list metadata is shown below. You can edit or remove it.
                                                </Typography>
                                            </Alert>
                                        )}

                                        {/* Add new metadata field */}
                                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                            <TextField
                                                size="small"
                                                placeholder="Key (e.g., importFile, apiName)"
                                                value={newSourceMetadata.key}
                                                onChange={(e) => setNewSourceMetadata({ ...newSourceMetadata, key: e.target.value })}
                                                sx={{ flex: 1 }}
                                            />
                                            <TextField
                                                size="small"
                                                placeholder="Value"
                                                value={newSourceMetadata.value}
                                                onChange={(e) => setNewSourceMetadata({ ...newSourceMetadata, value: e.target.value })}
                                                sx={{ flex: 1 }}
                                            />
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    if (newSourceMetadata.key.trim() && newSourceMetadata.value.trim()) {
                                                        setSourceMetadata([...sourceMetadata, newSourceMetadata]);
                                                        setNewSourceMetadata({ key: '', value: '' });
                                                    }
                                                }}
                                                disabled={!newSourceMetadata.key.trim() || !newSourceMetadata.value.trim()}
                                            >
                                                Add
                                            </Button>
                                        </Stack>

                                        {/* Display added metadata fields */}
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                            {sourceMetadata.map((field, index) => (
                                                <Chip
                                                    key={index}
                                                    label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography variant="caption" >
                                                            {field.key}:
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {field.value.length > 15 ? `${field.value.substring(0, 15)}...` : field.value}
                                                        </Typography>
                                                    </Box>}
                                                    onDelete={() => {
                                                        const updated = [...sourceMetadata];
                                                        updated.splice(index, 1);
                                                        setSourceMetadata(updated);
                                                    }}

                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        color: 'secondary.contrastText',
                                                        borderColor: 'secondary.contrastText',
                                                        '& .MuiChip-label': {
                                                            fontSize: '0.75rem',
                                                            px: 1,
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                </Stack>
                            </Paper>

                            {/* Action Buttons */}
                            <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        navigate('/inventory');
                                        reset();
                                        setSourceType('manual');
                                        setSourceId('');
                                        setImportBatchId('');
                                        setSourceMetadata([]);
                                        setNewSourceMetadata({ key: "", value: "" });
                                        setPriceListData(null);
                                        setDescriptionJsonFields([]);
                                        setNewJsonField({ key: "", value: "" });
                                    }}
                                    size="small"
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    sx={{ minWidth: 100 }}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={20} /> : 'Create Product'}
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </CardContent>

                {/* Edit Compatibility Dialog */}
                <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ pb: 1 }}>
                        <Typography variant="h6">Edit Vehicle Compatibility</Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Stack direction="row" spacing={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Car Brand</InputLabel>
                                    <Select
                                        value={selectedCarBrand?._id || ''}
                                        label="Car Brand"
                                        onChange={(e) => {
                                            const brand = carBrands.find(b => b._id === e.target.value);
                                            setSelectedCarBrand(brand || null);
                                        }}
                                    >
                                        {carBrands.map(brand => (
                                            <MenuItem key={brand._id} value={brand._id}>
                                                {brand.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Car Model</InputLabel>
                                    <Select
                                        value={selectedCarModel?._id || ''}
                                        label="Car Model"
                                        onChange={(e) => {
                                            const model = filteredCarModels.find(m => m._id === e.target.value);
                                            setSelectedCarModel(model || null);
                                        }}
                                    >
                                        {filteredCarModels.map(model => (
                                            <MenuItem key={model._id} value={model._id}>
                                                {model.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>

                            {selectedCarModel && (
                                <Stack spacing={2}>
                                    <Stack direction="row" spacing={2}>
                                        {selectedCarModel.variants.length > 0 && (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Variants</InputLabel>
                                                <Select
                                                    multiple
                                                    value={selectedVariants}
                                                    label="Variants"
                                                    onChange={(e) => setSelectedVariants(e.target.value as string[])}
                                                >
                                                    {selectedCarModel.variants.map(variant => (
                                                        <MenuItem key={variant} value={variant}>
                                                            <Checkbox checked={selectedVariants.indexOf(variant) > -1} />
                                                            {variant}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                        {selectedCarModel.fuelTypes.length > 0 && (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Fuel Types</InputLabel>
                                                <Select
                                                    multiple
                                                    value={selectedFuelTypes}
                                                    label="Fuel Types"
                                                    onChange={(e) => setSelectedFuelTypes(e.target.value as string[])}
                                                >
                                                    {selectedCarModel.fuelTypes.map(fuelType => (
                                                        <MenuItem key={fuelType} value={fuelType}>
                                                            <Checkbox checked={selectedFuelTypes.indexOf(fuelType) > -1} />
                                                            {fuelType}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    </Stack>
                                    <Stack direction="row" spacing={2}>
                                        {selectedCarModel.transmissions.length > 0 && (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Transmissions</InputLabel>
                                                <Select
                                                    multiple
                                                    value={selectedTransmissions}
                                                    label="Transmissions"
                                                    onChange={(e) => setSelectedTransmissions(e.target.value as string[])}
                                                >
                                                    {selectedCarModel.transmissions.map(transmission => (
                                                        <MenuItem key={transmission} value={transmission}>
                                                            <Checkbox checked={selectedTransmissions.indexOf(transmission) > -1} />
                                                            {transmission}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                        {selectedCarModel.generations.length > 0 && (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Generations</InputLabel>
                                                <Select
                                                    multiple
                                                    value={selectedGenerations}
                                                    label="Generations"
                                                    onChange={(e) => setSelectedGenerations(e.target.value as string[])}
                                                >
                                                    {selectedCarModel.generations.map((gen, index) => (
                                                        <MenuItem key={index} value={`${gen.from}-${gen.to}`}>
                                                            <Checkbox checked={selectedGenerations.indexOf(`${gen.from}-${gen.to}`) > -1} />
                                                            {gen.from} - {gen.to}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setEditDialogOpen(false)} size="small">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpdateCompatibility}
                            variant="contained"
                            disabled={!selectedCarModel}
                            size="small"
                        >
                            Update
                        </Button>
                    </DialogActions>
                </Dialog>
            </Card>
        </>
    );
};

export default AddProduct;